/**
 * Section: Menu drawer
 * -----------------------------------------------------------------------------
 * Scripts to initiate menu drawer toggle and also display on admin.
 *
 * @namespace menuDrawer
 */
import {register} from '@shopify/theme-sections';
import {clearAllBodyScrollLocks, disableBodyScroll} from 'body-scroll-lock';

import cssClasses from '~/helpers/cssClasses';
import {on, getSiblings} from '~/helpers/utils';
import Toggle from '~/components/toggle';

/**
 * DOM selectors.
 */
const selectors = {
  body: '[js-menu-drawer="body"]',
  tier2Menu: '[js-menu-drawer="tier2Menu"]',
  tier2MenuToggle: '[js-menu-drawer="tier2MenuToggle"]',
  container: '[js-menu-drawer="container"]',
  tier3MenuToggle: '[js-menu-drawer="tier3MenuToggle"]',
  close: '[js-menu-drawer="close"]',
};

/**
 * Instantiate menu drawer toggle.
 */
const MenuDrawer = Toggle({
  toggleSelector: 'menuDrawer',
});

/**
 * Component selectors.
 */
const menus = [
  ...document.querySelectorAll(selectors.tier2MenuToggle),
  ...document.querySelectorAll(selectors.tier3MenuToggle),
];

/**
 * Menu drawer section constructor.
 * Executes on page load as well as Theme Editor `section:load` events.
 */
register('menu-drawer', {

  /**
   * Set cached node selectors.
   */
  setNodeSelectors() {
    this.body = document.querySelector(selectors.body);
    this.container = document.querySelector(selectors.container);
    this.close = [...document.querySelectorAll(selectors.close)];
  },

  /**
   * Initiate toggle events on all sub-menus.
   */
  setEventListeners() {
    [...menus].forEach((element) => {
      on('click', element, (event) => this.onItemClick(event, element));
    });

    this.close.forEach((element) => {
      on('click', element, (event) => this.onSubNavClose(event, element));
    });
  },

  /**
   * Handle toggle event on menu item click.
   * @param {Event} event - Click event.
   * @param {HTMLElement} element - The menu item.
   */
  onItemClick(event, element) {
    event.preventDefault();
    this.toggleElement(element);
    this.closeSiblings(element);
    this.toggleElement(getSiblings(element)[0]);
  },

  /**
   * Handle toggle event on menu item click.
   * @param {Event} event - Click event.
   * @param {HTMLElement} element - The menu item.
   */
  onSubNavClose(event, element) {
    const parent = element.parentNode.parentNode;
    const nav = element.parentNode;

    event.preventDefault();
    nav.classList.remove(cssClasses.active);
    parent.classList.remove(cssClasses.active);
    disableBodyScroll(this.body);
  },

  /**
   * Set EventBus listeners for each instance.
   */
  setEventBusListeners() {
    Frame.EventBus.listen(['Toggle:menuDrawer:open'], () => this.handleDrawerOpen());
    Frame.EventBus.listen(['Toggle:menuDrawer:close'], () => clearAllBodyScrollLocks());
  },

  /**
   * Handle menu drawer open EventBus event.
   * Checks if any tier2 menus are open.
   * - If so, disable body scroll on the tier2 menu.
   * - If not, disable body scroll on the menu drawer body.
   */
  handleDrawerOpen() {
    const activeTier2Menu = this.getActiveTier2Menu();

    if (activeTier2Menu) {
      disableBodyScroll(activeTier2Menu);
      return;
    }

    disableBodyScroll(this.body);
  },

  /**
   * Gets the active tier2 menu.
   * @returns {HTMLElement|null} - The active tier2 menu.
   */
  getActiveTier2Menu() {
    const tier2Menus = [...document.querySelectorAll(selectors.tier2Menu)];

    return tier2Menus.find((element) => element.classList.contains(cssClasses.active));
  },

  /**
   * Check if item is active.
   * @param {HTMLElement} element - The menu item.
   * @returns {Boolean}
   */
  isItemActive(element) {
    return element.classList.contains(cssClasses.active);
  },

  /**
   * Toggle button state.
   * @param {HTMLElement} element - The toggled button.
   */
  toggleElement(element) {
    if (this.isItemActive(element)) {
      this.hideMenu(element);
      return;
    }

    this.showMenu(element);
  },

  /**
   * Close sibling elements in secondary nav.
   * @param {HTMLElement} element - The toggled button.
   */
  closeSiblings(element) {
    const isTier3MenuToggle = (
      element.hasAttribute('js-menu-drawer') &&
      (element.getAttribute('js-menu-drawer') === 'tier3MenuToggle')
    );

    if (!isTier3MenuToggle) {
      return;
    }

    const allChildren = getSiblings(element.parentElement);

    allChildren.forEach((child) => {
      if (!child.classList.contains('menu-drawer__item--secondary')) {
        return;
      }

      const menuButton = child.children[0];
      const tier3MenuList = child.children[1];

      const isCurrentElement = (element === menuButton);

      if (!isCurrentElement && this.isItemActive(tier3MenuList)) {
        this.hideMenu(tier3MenuList);
        this.hideMenu(menuButton);
      }
    });
  },

  /**
   * Hide menu.
   * @param {HTMLElement} element - The item to hide.
   */
  hideMenu(element) {
    element.classList.remove(cssClasses.active);
  },

  /**
   * Show menu.
   * @param {HTMLElement} element - The item to show.
   */
  showMenu(element) {
    element.classList.add(cssClasses.active);

    if (
      element.hasAttribute('js-menu-drawer') &&
      element.getAttribute('js-menu-drawer') !== 'tier2Menu'
    ) {
      return;
    }

    disableBodyScroll(element);
  },

  /**
   * Initialise section.
   */
  init() {
    MenuDrawer.init();
    this.setNodeSelectors();
    this.setEventListeners();
  },

  /**
   * Event callback for Theme Editor `section:select` event
   */
  onSelect() {
    MenuDrawer.open();
  },

  /**
   * Event callback for Theme Editor `shopify:section:deselect` event.
   */
  onDeselect() {
    MenuDrawer.close();
  },

  /**
   * Event callback for Theme Editor `section:load` event
   */
  onLoad() {
    this.init();
    this.setEventBusListeners();
  },
});
